import { z } from 'zod';

export const saveDesignSchema = z.object({
  product_id:            z.string().min(1),
  associated_product_id: z.string().optional(),
  customer_id:           z.string().optional(),
  // "My Design" retention key for guests (Design.sessionId) — see
  // backend/src/routes/designs.ts POST / for how this is used and ignored.
  sessionId:             z.string().optional(),
  title:                 z.string().max(255).optional(),
  canvasJson:            z.string().min(1),
  previewJson:           z.string().min(1),
  dataUrl:               z.record(z.string(), z.string()).optional(),
  product_image_data:    z.any().optional(),
  imageWidth:            z.number().positive(),
  imageHeight:           z.number().positive(),
  productImageWidth:     z.number().positive().optional(),
  productImageHeight:    z.number().positive().optional(),
  canvasWidth:           z.number().positive().optional(),
  canvasHeight:          z.number().positive().optional(),
  scaleFactor:           z.number().positive().optional(),
  outputWidth:           z.number().optional(),
  outputHeight:          z.number().optional(),
  outputWidthUnit:       z.string().optional(),
  outputHeightUnit:      z.string().optional(),
  defaultUnit:           z.string().optional(),
  customSizePrice:       z.number().optional(),
  outputType:            z.string().optional(),
  bleedLine:             z.any().optional(),
  prices:                z.any().optional(),
  printingMethodData:    z.any().optional(),
  name_number:           z.string().optional(),
  vdp_data:              z.string().optional(),
  design_id:             z.string().optional(),
  customer_comments:     z.string().max(2000).optional(),
  cartData:              z.array(z.any()).optional(),
  customizePrices:       z.any().optional(),
  custom_option:         z.any().optional(),
  qty:                   z.number().int().positive().optional(),
  // Distinguishes the header's "Save" button from the cart popup's "Add to
  // Cart" — both post here with an otherwise identical shape; the dashboard's
  // save-vs-checkout funnel metrics rely on this to tell them apart.
  source:                z.enum(['save', 'cart']).optional(),
  // Object Variants the customer picked in the 3D preview (full tab or mini
  // widget), keyed by ProductVariant id; each value is a curated
  // ProductVariantOption id or `custom:#rrggbb` from the free color picker.
  // Persisted so the approval page / admin drawer can show the design on the
  // exact variant the customer chose, not the model's default appearance.
  selectedVariantOptions: z.record(z.string(), z.string()).optional(),
  // The customer's chosen vendor Product Color Variant (ProductOptionsModal's
  // "Product color" pill / `?colorVariant=` launch param) — distinct from
  // selectedVariantOptions above, which is the separate 3D Object Variant
  // system. Without this, no consumer that reloads a saved design (widget
  // preview, approval page, admin PDF/PNG download) can tell which color the
  // customer actually picked, and useProductSetup's own "no match" fallback
  // silently defaults back to the product's first color variant.
  colorVariantId:         z.string().optional(),
  // Pre-composited preview image (PNG data URL) of the design as the customer
  // last saw it — the 3D model screenshot with their variant selection baked
  // in when the product has a GLB, else the 2D product-photo composite. Stored
  // as the design's 'base' image (cart/order thumbnail + admin drawer preview).
  previewImageDataUrl:    z.string().optional(),
});

export const loadDesignSchema = z.object({
  designId: z.string().min(1),
});

// POST /api/designs/wip — "Design WIP" placeholder creation, see
// backend/src/routes/designs.ts for the full flow: the frontend mints a uuid
// the moment the editor opens (before the customer has touched anything) and
// this creates the row immediately so it's visible in the admin Design WIP
// grid from second one, not just after the first 30s autosave.
export const createWipDesignSchema = z.object({
  wipId:      z.string().min(1),
  product_id: z.string().min(1),
  sessionId:  z.string().optional(),
});

export const getClipartSchema = z.object({
  clipart_id: z.string().min(1),
  search:     z.string().max(100).optional(),
  limit:      z.coerce.number().int().min(1).max(100).default(20),
  page:       z.coerce.number().int().min(1).default(1),
});

export const getArtworkSchema = z.object({
  category_id: z.string().optional(),
  search:      z.string().max(100).optional(),
  limit:       z.coerce.number().int().min(1).max(100).default(20),
  page:        z.coerce.number().int().min(1).default(1),
});

export const getTemplateSchema = z.object({
  product_id:             z.string().optional(),
  associated_product_id:  z.string().optional(),
  search:                 z.string().max(100).optional(),
  page:                   z.coerce.number().int().min(1).default(1),
});

export const previewRequestSchema = z.object({
  dataUrl:            z.string().min(1),
  product_image_data: z.object({
    url:        z.string().min(1),
    bleedLine:  z.number().optional(),
  }),
  hasObjects:     z.boolean().optional().default(true),
  bleedLine:      z.number().optional(),
  watermarkImages: z.boolean().optional().default(true),
  unLoadedPreview: z.boolean().optional().default(false),
  storeId:        z.number().optional(),
  imageWidth:     z.number().positive(),
  imageHeight:    z.number().positive(),
});

export const downloadRequestSchema = z.object({
  dataUrl:          z.union([z.string(), z.record(z.string())]),
  unLoadedPreview:  z.union([z.string(), z.array(z.string())]).optional(),
  product_image_data: z.any(),
  bleedLine:        z.any().optional(),
  previewImages:    z.union([z.literal(0), z.literal(1)]),
  watermarkImages:  z.boolean().optional().default(true),
  storeId:          z.string().optional(),
});

export type SaveDesignInput    = z.infer<typeof saveDesignSchema>;
export type GetClipartInput    = z.infer<typeof getClipartSchema>;
export type PreviewRequestInput = z.infer<typeof previewRequestSchema>;
export type DownloadRequestInput = z.infer<typeof downloadRequestSchema>;
